import React from 'react';
import { Card, SimpleGrid, Stack, Text, ThemeIcon, Group, Progress, Badge } from '@mantine/core';
import { 
  IconClock, 
  IconMoodSmile, 
  IconToolsKitchen2, 
  IconMoon,
  IconActivity,
  IconShieldCheck,
  IconSun
} from '@tabler/icons-react';
import { useTodayActivities } from '../../../hooks/parent/useChildActivities';

interface ChildQuickStatsProps {
  childId: string;
}

export const ChildQuickStats: React.FC<ChildQuickStatsProps> = ({ childId }) => {
  const { data: activitiesData } = useTodayActivities(childId);
  const activities = Array.isArray(activitiesData) ? activitiesData : (activitiesData?.items || []);

  const stats = {
    arrivalTime: activities.find(a => a.activity_type === 'arrival')?.timestamp || null,
    mood: 'Happy', // This would come from the latest mood check
    mealsEaten: activities.filter(a => ['meal', 'eating', 'breakfast', 'lunch', 'snack'].includes(a.activity_type)).length,
    napTime: activities.find(a => ['nap', 'sleep', 'rest'].includes(a.activity_type)),
    totalActivities: activities.length,
    safetyStatus: 'Excellent',
    lastActivity: activities[activities.length - 1],
  };

  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return 'Not recorded';
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit' 
    });
  };

  return (
    <Card withBorder>
      <Stack gap="md">
        <Text size="lg" fw={600}>Today's Overview</Text>
        
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Group>
            <ThemeIcon size="lg" variant="light" color="green">
              <IconClock size={20} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Arrival Time</Text>
              <Text fw={500}>{formatTime(stats.arrivalTime)}</Text>
            </div>
          </Group>

          <Group>
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconMoodSmile size={20} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Current Mood</Text>
              <Badge color="green" variant="light" size="sm">{stats.mood}</Badge>
            </div>
          </Group>

          <Group>
            <ThemeIcon size="lg" variant="light" color="orange">
              <IconToolsKitchen2 size={20} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Meals Today</Text>
              <Text fw={500}>{stats.mealsEaten} / 3</Text>
            </div>
          </Group>

          <Group>
            <ThemeIcon size="lg" variant="light" color="violet">
              <IconMoon size={20} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Nap Time</Text>
              <Text fw={500} size="sm">
                {stats.napTime ? formatTime(stats.napTime.timestamp) : 'Not yet'}
              </Text>
            </div>
          </Group>

          <Group>
            <ThemeIcon size="lg" variant="light" color="teal">
              <IconActivity size={20} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Activities</Text>
              <Text fw={500}>{stats.totalActivities} today</Text>
            </div>
          </Group>

          <Group>
            <ThemeIcon size="lg" variant="light" color="red">
              <IconShieldCheck size={20} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed">Safety</Text>
              <Badge color="green" variant="light" size="sm">{stats.safetyStatus}</Badge>
            </div>
          </Group>
        </SimpleGrid>

        {/* Activity Progress Bar */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm">Daily Activity Progress</Text>
            <Text size="sm" c="dimmed">{stats.totalActivities} activities</Text>
          </Group>
          <Progress value={(stats.totalActivities / 20) * 100} color="blue" size="md" radius="xl" />
        </div>

        {/* Last Activity */}
        {stats.lastActivity && (
          <Card withBorder p="sm" bg="gray.0">
            <Group justify="space-between">
              <Group gap="xs">
                <ThemeIcon size="sm" variant="light" color="blue">
                  <IconSun size={14} />
                </ThemeIcon>
                <div>
                  <Text size="xs" c="dimmed">Latest Activity</Text>
                  <Text size="sm" fw={500}>{stats.lastActivity.description}</Text>
                </div>
              </Group>
              <Text size="xs" c="dimmed">
                {formatTime(stats.lastActivity.timestamp)}
              </Text>
            </Group>
          </Card>
        )}
      </Stack>
    </Card>
  );
};

export default ChildQuickStats;